import React, { useState } from 'react';
import { Book, Beaker, Bookmark, GraduationCap, Lightbulb, Wheat, ChevronRight, Cloud as Flour, Droplet, Salad as Salt, Microscope, Leaf, Flame, Scissors, AlertTriangle, Video, Calculator, Share2 } from 'lucide-react';
import { PremiumFeature } from '../components/subscription/PremiumFeature';

const FACEBOOK_GROUP_URL = 'https://www.facebook.com';

// Import knowledge module pages
import { FlourTypes } from '../components/knowledge/ingredients/FlourTypes';
import { FlourHydration } from '../components/knowledge/ingredients/FlourHydration';
import { WaterTypes } from '../components/knowledge/ingredients/WaterTypes';
import { SaltTypes } from '../components/knowledge/ingredients/SaltTypes';
import { FermentationTypes } from '../components/knowledge/fermentation/FermentationTypes';
import { FermentationApproaches } from '../components/knowledge/fermentation/FermentationApproaches';
import { StarterFeeding } from '../components/knowledge/starter/StarterFeeding';
import { StarterPreservation } from '../components/knowledge/starter/StarterPreservation';
import { AutolyseTechnique } from '../components/knowledge/baking/AutolyseTechnique';
import { KneadingTechniques } from '../components/knowledge/baking/KneadingTechniques';
import { BakingProcess } from '../components/knowledge/baking/BakingProcess';
import { ScoringTechniques } from '../components/knowledge/baking/ScoringTechniques';
import { CommonMistakes } from '../components/knowledge/troubleshoot/CommonMistakes';
import { FermentationIssues } from '../components/knowledge/troubleshoot/FermentationIssues';
import { ShapingIssues } from '../components/knowledge/troubleshoot/ShapingIssues';
import { BakingIssues } from '../components/knowledge/troubleshoot/BakingIssues';
import { WaterTemperatureCalculator } from '../components/knowledge/calculators/WaterTemperatureCalculator'; 
import { StarterCalculator } from '../components/knowledge/calculators/StarterCalculator'; 
import { SessionTimingCalculator } from '../components/knowledge/calculators/SessionTimingCalculator';
import { SlowFermentationCalculator } from '../components/knowledge/calculators/SlowFermentationCalculator';

export function KnowledgePage() {
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

  const handleShareResults = () => {
    window.open(FACEBOOK_GROUP_URL, '_blank', 'noopener,noreferrer');
  };

  const modules = [
    {
      id: 'ingredients',
      title: 'Ingredients',
      icon: Wheat,
      topics: [
        { id: 'flour-types', title: 'Flour Types', component: FlourTypes },
        { id: 'flour-hydration', title: 'Finding the Right Hydration Rate', component: FlourHydration },
        { id: 'water-types', title: 'Water Types', component: WaterTypes },
        { id: 'salt-types', title: 'Salt Types', component: SaltTypes }
      ]
    },
    {
      id: 'fermentation',
      title: 'Fermentation',
      icon: Microscope,
      topics: [
        { id: 'fermentation-types', title: 'Different Types of Ferments', component: FermentationTypes },
        { id: 'fermentation-approaches', title: 'Basic Fermentation Approaches', component: FermentationApproaches }
      ]
    },
    {
      id: 'starter',
      title: 'Sourdough Starter',
      icon: Beaker,
      topics: [
        { id: 'starter-feeding', title: 'Feeding Your Starter', component: StarterFeeding },
        { id: 'starter-preservation', title: 'Preserving Your Starter', component: StarterPreservation }
      ]
    },
    {
      id: 'baking',
      title: 'Baking the Heritage Way',
      icon: Flame,
      topics: [
        { id: 'autolyse', title: 'Autolyse Technique', component: AutolyseTechnique },
        { id: 'kneading', title: 'Kneading Techniques', component: KneadingTechniques },
        { id: 'baking-process', title: 'Baking Process', component: BakingProcess },
        { id: 'scoring', title: 'Scoring Techniques', component: ScoringTechniques }
      ]
    },
    {
      id: 'troubleshoot',
      title: 'Troubleshoot',
      icon: AlertTriangle,
      topics: [
        { id: 'common-mistakes', title: 'Common Mistakes', component: CommonMistakes },
        { id: 'fermentation-issues', title: 'Fermentation Issues', component: FermentationIssues },
        { id: 'shaping-issues', title: 'Shaping Issues', component: ShapingIssues },
        { id: 'baking-issues', title: 'Baking Issues', component: BakingIssues }
      ]
    }
  ];

  // Hidden calculators module - accessible via Resources section
  const calculatorsModule = {
    id: 'calculators',
    title: 'Calculators',
    icon: Calculator,
    topics: [
      { id: 'slow-fermentation-calculator', title: 'Slow Fermentation Starter Calculator', component: SlowFermentationCalculator },
      { id: 'water-temp-calculator', title: 'Water Temperature Calculator', component: WaterTemperatureCalculator },
      { id: 'starter-calculator', title: 'Starter Quantity Calculator', component: StarterCalculator },
      { id: 'session-timing-calculator', title: 'Session Timing Simulator', component: SessionTimingCalculator }
    ]
  };

  const featuredArticles = [
    {
      id: 1,
      title: "Understanding Sourdough Fermentation",
      description: "Learn about the science behind sourdough fermentation and how to control it for better results.",
      category: "Science",
      icon: Lightbulb,
      readTime: "8 min read",
      module: "fermentation",
      topic: "fermentation-types"
    },
    {
      id: 2,
      title: "Mastering Starter Maintenance",
      description: "Tips and techniques for keeping your sourdough starter healthy and active year-round.",
      category: "Techniques",
      icon: Wheat,
      readTime: "6 min read",
      module: "starter",
      topic: "starter-feeding"
    },
    {
      id: 3,
      title: "Advanced Scoring Patterns",
      description: "Take your bread aesthetics to the next level with these decorative scoring techniques.",
      category: "Techniques",
      icon: Scissors,
      readTime: "10 min read",
      module: "baking",
      topic: "scoring"
    },
    {
      id: 4,
      title: "Troubleshooting Common Sourdough Problems",
      description: "Solutions for dense crumb, poor oven spring, and other common sourdough challenges.",
      category: "Troubleshooting",
      icon: AlertTriangle,
      readTime: "12 min read",
      module: "troubleshoot",
      topic: "common-mistakes"
    }
  ];

  const handleModuleSelect = (moduleId: string) => {
    setSelectedModule(moduleId);
    setSelectedTopic(null);
  };

  const handleTopicSelect = (moduleId: string, topicId: string) => {
    setSelectedModule(moduleId);
    setSelectedTopic(topicId);
  };

  const renderContent = () => {
    if (selectedTopic) {
      const module = selectedModule === 'calculators' 
        ? calculatorsModule 
        : modules.find(m => m.id === selectedModule);
      if (!module) return null;
      
      const topic = module.topics.find(t => t.id === selectedTopic);
      if (!topic) return null;
      
      const TopicComponent = topic.component;
      return (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-8">
            <button 
              onClick={() => setSelectedTopic(null)} 
              className="text-amber-400 hover:text-amber-300 transition-colors"
            >
              ← Back to {module.title}
            </button>
          </div>
          <h2 className="text-2xl text-white font-brand font-light italic mb-4">{topic.title}</h2>
          <TopicComponent />
        </div>
      );
    }

    if (selectedModule) {
      const module = selectedModule === 'calculators' 
        ? calculatorsModule 
        : modules.find(m => m.id === selectedModule);
      if (!module) return null;
      
      return (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-8">
            <button 
              onClick={() => setSelectedModule(null)} 
              className="text-amber-400 hover:text-amber-300 transition-colors"
            >
              ← Back to Knowledge Centre
            </button>
          </div>
          <h2 className="text-2xl text-white font-brand font-light italic mb-4">{module.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {module.topics.map(topic => (
              <div 
                key={topic.id}
                onClick={() => handleTopicSelect(module.id, topic.id)}
                className="bg-gray-600/60 p-6 cursor-pointer hover:border hover:border-amber-400 transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-xl text-white font-brand font-light italic">{topic.title}</h3>
                  <ChevronRight className="w-5 h-5 text-amber-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Featured Article */}
        <div className="bg-gray-600/60 overflow-hidden shadow-lg mb-8">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/2 p-8 flex flex-col justify-center">
              <h2 className="text-3xl text-white font-brand font-light italic mb-4">The Art and Science of Sourdough</h2>
              <p className="text-white/80 mb-6">
                Discover the perfect balance between artisanal techniques and scientific principles to elevate your sourdough baking to new heights.
              </p>
              <button 
                onClick={() => handleTopicSelect('fermentation', 'fermentation-types')}
                className="px-6 py-3 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-xl w-fit transition-all duration-300"
              >
                Read Article
              </button>
            </div>
            <div className="md:w-1/2 h-[300px] md:h-auto relative">
              <img 
                src="/background.png" 
                alt="Sourdough bread" 
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Knowledge Modules */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {modules.map(module => (
            <div 
              key={module.id}
              onClick={() => handleModuleSelect(module.id)}
              className="bg-gray-600/60 overflow-hidden shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer hover:border hover:border-amber-400"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <module.icon className="w-8 h-8 text-amber-400" strokeWidth="1.5" />
                  <h3 className="text-xl text-white font-brand font-light italic">
                    {module.title}
                  </h3>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          ))}
        </div>

        {/* Featured Articles */}
        <h2 className="text-2xl text-white font-brand font-light italic mb-4">Featured Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {featuredArticles.map(article => (
            <div 
              key={article.id}
              onClick={() => handleTopicSelect(article.module, article.topic)}
              className="bg-gray-600/60 overflow-hidden shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer hover:border hover:border-amber-400"
            >
              <div className="flex items-center gap-2 mb-4">
                <article.icon className="w-5 h-5 text-amber-400" />
                <span className="text-sm text-white/70">{article.category}</span>
              </div>
              <h3 className="text-xl text-white font-brand font-light italic mb-2">{article.title}</h3>
              <p className="text-white/80 mb-4 text-sm">{article.description}</p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/60">{article.readTime}</span>
                <button className="text-amber-400 hover:text-amber-300 text-sm">Read more</button>
              </div>
            </div>
          ))}
        </div>

        {/* Resources Section */}
        <h2 className="text-2xl text-white font-brand font-light italic mb-4">Recommended Resources</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div 
            onClick={() => handleModuleSelect('calculators')}
            className="bg-gray-600/60 overflow-hidden shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer hover:border hover:border-amber-400"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-5 h-5 text-amber-400" />
              <h3 className="text-xl text-white font-brand font-light italic">Sourdough Calculators</h3>
            </div>
            <p className="text-white/80 text-sm">Tools to help you calculate hydration, baker's percentages, and feeding schedules.</p>
          </div>
          <div 
            onClick={handleShareResults}
            className="bg-gray-600/60 overflow-hidden shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer hover:border hover:border-amber-400"
          >
            <div className="flex items-center gap-3 mb-4">
              <Share2 className="w-5 h-5 text-amber-400" />
              <h3 className="text-xl text-white font-brand font-light italic">Join Our Baking Community</h3>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/80 text-sm">Connect with fellow bakers, share your creations, and learn from each other in our Facebook group.</p>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PremiumFeature featureName="Knowledge Centre">
        <div className="flex justify-between items-start mb-8 mt-4">
          <div>
            <h1 className="text-3xl text-white font-brand font-light italic">Knowledge Centre</h1>
            <p className="text-sm text-white mt-1">
              Explore articles, guides, and resources to improve your sourdough baking skills.
            </p>
          </div>
        </div>

        {renderContent()}
      </PremiumFeature>
    </div>
  );
}